import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';

import { articles, articlesBySlug } from '../../docs/index';
import DocsContent from './DocsContent';
import DocsSidebar from './DocsSidebar';
import './style.css';

import * as introducereModule from '../../docs/content/introducere';
import * as temaModule from '../../docs/content/tema';
import * as contModule from '../../docs/content/cont';
import * as abonamenteModule from '../../docs/content/abonamente';
import * as facturiModule from '../../docs/content/facturi-si-plati';
import * as macheteModule from '../../docs/content/machete';
import * as firmeModule from '../../docs/content/firme';
import * as generareModule from '../../docs/content/generare-intrastat';
import * as faqModule from '../../docs/content/faq';
import * as incarcaModule from '../../docs/content/incarca-intrastat';

const modulesBySlug = {
  introducere: introducereModule,
  tema: temaModule,
  cont: contModule,
  abonamente: abonamenteModule,
  'facturi-si-plati': facturiModule,
  machete: macheteModule,
  firme: firmeModule,
  'generare-intrastat': generareModule,
  faq: faqModule,
  'incarca-intrastat': incarcaModule,
};

const extractHeadings = (md) =>
  md
    .split('\n')
    .filter((l) => /^#{2,4}\s/.test(l))
    .map((l) => ({
      level: l.match(/^(#{2,4})/)[1].length,
      text: l.replace(/^#{2,4}\s+/, ''),
      id: l
        .replace(/^#{2,4}\s+/, '')
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-'),
    }));

const DocsLayout = () => {
  const { slug } = useParams();
  const [activeHeadingId, setActiveHeadingId] = useState('');

  // All hooks must be called before any early returns
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const headingEls = document.querySelectorAll(
      '.docs-content h2[id], .docs-content h3[id], .docs-content h4[id]'
    );
    if (!headingEls.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
          }
        });
      },
      { rootMargin: '-10% 0px -80% 0px', threshold: 0 }
    );

    headingEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [slug]);

  const mod = modulesBySlug[slug];
  if (!mod) return <Navigate to="/docs" replace />;

  const { content, images = {} } = mod;
  const meta = articlesBySlug[slug];
  const headings = extractHeadings(content);

  const relatedArticles = (meta.related || [])
    .map((s) => articlesBySlug[s])
    .filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{meta.title} — Documentație Programintrastat.ro</title>
      </Helmet>

      <div className="docs-layout">
        <DocsSidebar
          articles={articles}
          currentSlug={slug}
          headings={headings}
          activeHeadingId={activeHeadingId}
        />

        <main>
          <Link to="/docs" className="docs-back-link">
            <FontAwesomeIcon icon={faChevronLeft} />
            &nbsp;Înapoi la documentație
          </Link>
          <DocsContent content={content} images={images} />
        </main>

        {relatedArticles.length > 0 && (
          <aside className="docs-related">
            <span className="docs-sidebar-label">Articole conexe</span>
            {relatedArticles.map((a) => (
              <Link key={a.slug} to={`/docs/${a.slug}`} className="docs-related-card">
                <strong>{a.title}</strong>
                <p>{a.description}</p>
              </Link>
            ))}
          </aside>
        )}
      </div>
    </>
  );
};

export default DocsLayout;
